import { useState } from 'react'
import { isAxiosError } from 'axios'
import { useForm } from 'react-hook-form'
import {
  useStaffMembers,
  useStaffInvites,
  useSetStaffActive,
  useCreateStaffInvite,
  useRevokeStaffInvite,
} from '@/hooks/backoffice/useStaff'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { StatusBadge } from '@/components/ui/status-badge'
import { CopyableLink } from '@/components/CopyableLink'
import type { StaffRole, CreateStaffInviteResult } from '@/types/backoffice'

const ROLE_LABEL: Record<StaffRole, string> = { OWNER: 'Owner', STAFF: 'Operador' }

interface InviteForm {
  email: string
  role: StaffRole
}

export function StaffPage() {
  const { data: members, isLoading: loadingMembers } = useStaffMembers()
  const { data: invites, isLoading: loadingInvites } = useStaffInvites()
  const setActive = useSetStaffActive()
  const createInvite = useCreateStaffInvite()
  const revoke = useRevokeStaffInvite()

  const [created, setCreated] = useState<CreateStaffInviteResult | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<InviteForm>({ defaultValues: { email: '', role: 'STAFF' } })

  async function onInvite(data: InviteForm) {
    setApiError(null)
    try {
      const res = await createInvite.mutateAsync(data)
      setCreated(res)
      reset({ email: '', role: 'STAFF' })
    } catch (err) {
      const msg = isAxiosError(err) ? (err.response?.data?.error?.message as string | undefined) : undefined
      setApiError(msg ?? 'Erro ao criar convite.')
    }
  }

  async function handleToggle(id: string, active: boolean) {
    if (!confirm(active ? 'Reativar operador?' : 'Desativar operador?')) return
    await setActive.mutateAsync({ id, active })
  }

  async function handleRevoke(id: string) {
    if (!confirm('Revogar convite pendente?')) return
    await revoke.mutateAsync(id)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">Equipe da plataforma</h1>
        <p className="text-sm text-muted-foreground">Operadores que têm acesso ao backoffice.</p>
      </div>

      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <h2 className="text-sm font-semibold text-foreground">Convidar novo operador</h2>
        <form onSubmit={handleSubmit(onInvite)} noValidate className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1 space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              {...register('email', {
                required: 'Obrigatório',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email inválido' },
              })}
            />
            {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
          </div>
          <div className="space-y-1.5 sm:w-40">
            <Label htmlFor="role">Role</Label>
            <select
              id="role"
              {...register('role')}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm"
            >
              <option value="STAFF">Operador</option>
              <option value="OWNER">Owner</option>
            </select>
          </div>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Criando…' : 'Criar convite'}
          </Button>
        </form>
        {apiError && (
          <p className="text-xs text-destructive">{apiError}</p>
        )}
        {created && (
          <div className="space-y-1 border-t border-border pt-3">
            <p className="text-sm text-foreground">
              Convite gerado para <span className="font-medium">{created.invite.email}</span>:
            </p>
            <CopyableLink
              url={created.acceptUrl}
              hint={`Expira em ${new Date(created.invite.expiresAt).toLocaleString('pt-BR')}.`}
            />
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground">Operadores</h2>
        {loadingMembers && <p className="text-sm text-muted-foreground">Carregando…</p>}
        {members && members.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Nome</th>
                  <th className="px-3 py-2 text-left font-medium">Email</th>
                  <th className="px-3 py-2 text-left font-medium">Role</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-t border-border">
                    <td className="px-3 py-2">{m.name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{m.email}</td>
                    <td className="px-3 py-2">{ROLE_LABEL[m.role]}</td>
                    <td className="px-3 py-2">
                      <StatusBadge active={m.active} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant={m.active ? 'destructive' : 'default'}
                        onClick={() => handleToggle(m.id, !m.active)}
                      >
                        {m.active ? 'Desativar' : 'Reativar'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-foreground">Convites</h2>
        {loadingInvites && <p className="text-sm text-muted-foreground">Carregando…</p>}
        {invites && invites.length === 0 && (
          <p className="text-sm text-muted-foreground">Nenhum convite cadastrado.</p>
        )}
        {invites && invites.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Email</th>
                  <th className="px-3 py-2 text-left font-medium">Role</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-left font-medium">Expira em</th>
                  <th className="px-3 py-2 text-right font-medium">Ações</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((i) => (
                  <tr key={i.id} className="border-t border-border">
                    <td className="px-3 py-2">{i.email}</td>
                    <td className="px-3 py-2">{ROLE_LABEL[i.role]}</td>
                    <td className="px-3 py-2">{i.status}</td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {new Date(i.expiresAt).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {i.status === 'PENDING' && (
                        <Button size="sm" variant="outline" onClick={() => handleRevoke(i.id)}>
                          Revogar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
